import type { BlazeCheckoutConfigDTO, BlazeCheckoutKind } from '@clanfire/shared';

/**
 * Paddle's checkout for ClanFire Blaze. Paddle takes the payment in its own
 * overlay (card details never touch ClanFire), so its script is only loaded
 * when someone opens the Blaze page while Blaze is on sale.
 */

const SCRIPT_URL = 'https://cdn.paddle.com/paddle/v2/paddle.js';

interface PaddleEventData {
  name?: string;
}

interface PaddleJs {
  Environment: { set(env: 'sandbox' | 'production'): void };
  Initialize(options: { token: string; eventCallback?: (event: PaddleEventData) => void }): void;
  Checkout: { open(options: { transactionId: string; settings?: Record<string, unknown> }): void };
  PricePreview(request: { items: Array<{ priceId: string; quantity: number }> }): Promise<{
    data: { details: { lineItems: Array<{ price: { id: string }; totals: { total: string }; formattedTotals: { total: string } }> } };
  }>;
}

declare global {
  interface Window {
    Paddle?: PaddleJs;
  }
}

let loading: Promise<PaddleJs> | null = null;
let initializedWith: string | null = null;
const listeners = new Set<(event: PaddleEventData) => void>();

function loadScript(): Promise<PaddleJs> {
  if (window.Paddle) return Promise.resolve(window.Paddle);
  loading ??= new Promise<PaddleJs>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => (window.Paddle ? resolve(window.Paddle) : reject(new Error('Paddle did not load')));
    script.onerror = () => {
      loading = null;
      script.remove();
      reject(new Error('Paddle did not load'));
    };
    document.head.appendChild(script);
  });
  return loading;
}

/** Paddle, ready to open checkouts (it's set up once, with the client-side token). */
export async function paddle(config: BlazeCheckoutConfigDTO): Promise<PaddleJs> {
  const Paddle = await loadScript();
  if (initializedWith !== config.clientToken) {
    if (config.environment === 'sandbox') Paddle.Environment.set('sandbox');
    Paddle.Initialize({ token: config.clientToken, eventCallback: (event) => listeners.forEach((l) => l(event)) });
    initializedWith = config.clientToken;
  }
  return Paddle;
}

/** Hears Paddle's checkout events (checkout.completed, checkout.closed…). */
export function onPaddleEvent(listener: (event: PaddleEventData) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Opens Paddle's checkout over the app, in the app's light or dark look. */
export async function openCheckout(config: BlazeCheckoutConfigDTO, transactionId: string) {
  const Paddle = await paddle(config);
  const dark = document.documentElement.classList.contains('dark');
  Paddle.Checkout.open({ transactionId, settings: { displayMode: 'overlay', theme: dark ? 'dark' : 'light', allowLogout: false } });
}

export interface LocalPrice {
  /** Ready to show, e.g. "£3.99". */
  text: string;
  /** In the smallest unit of the currency (pence), for working out savings. */
  amount: number;
}

/** Prices in the viewer's own currency (with tax as it applies to them), by option. */
export async function localPrices(config: BlazeCheckoutConfigDTO): Promise<Partial<Record<BlazeCheckoutKind, LocalPrice>>> {
  const Paddle = await paddle(config);
  const entries = Object.entries(config.prices).filter((e): e is [BlazeCheckoutKind, string] => !!e[1]);
  const preview = await Paddle.PricePreview({ items: entries.map(([, priceId]) => ({ priceId, quantity: 1 })) });
  const byPrice = new Map(preview.data.details.lineItems.map((li) => [li.price.id, { text: li.formattedTotals.total, amount: Number(li.totals.total) }]));
  return Object.fromEntries(entries.map(([kind, priceId]) => [kind, byPrice.get(priceId)]).filter(([, price]) => price));
}

/** How much less a year costs than twelve months, as a whole percentage (null if it can't be worked out). */
export function yearlySaving(prices: Partial<Record<BlazeCheckoutKind, LocalPrice>>): number | null {
  const month = prices.monthly?.amount;
  const year = prices.yearly?.amount;
  if (!month || !year || !Number.isFinite(month) || !Number.isFinite(year)) return null;
  return Math.round((1 - year / (12 * month)) * 100);
}
